import {HttpErrorResponse, HttpInterceptorFn} from "@angular/common/http";
import {catchError, EMPTY, filter, switchMap, take, throwError} from "rxjs";
import {inject} from "@angular/core";
import {AlertService} from "@core/services/alert.service";
import {StorageService} from "@core/services/storage.service";
import {Router} from "@angular/router";
import {AuthService} from "@core/services/auth.service";

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const router = inject(Router);
    const alertService = inject(AlertService);
    const storageService = inject(StorageService);
    const authService = inject(AuthService);

    // Não adicionar token para endpoints de auth
    const isAuthEndpoint = req.url.includes('/auth/login') || req.url.includes('/auth/refresh');

    const authReq = isAuthEndpoint ? req : req.clone({
        setHeaders: {
            Authorization: storageService.token ? `Bearer ${storageService.token}` : ''
        }
    });

    return next(authReq).pipe(
        catchError((err: any) => {
            if (err instanceof HttpErrorResponse) {
                switch (err.status) {
                    case 400:
                        console.error('🦈 Request 400:', err);
                        alertService.error('👾 Bad request', 'Please check the request parameters');
                        return throwError(() => err);

                    case 401:
                        console.error('🦈 Request 401:', err);

                        if (req.url.includes('/auth/login') || req.url.includes('/auth/refresh')) {
                            return throwError(() => err);
                        }

                        // Se já está fazendo refresh, esperar
                        if (authService.isRefreshingToken()) {
                            return authService.getRefreshTokenSubject().pipe(
                                filter(token => token !== null),
                                take(1),
                                switchMap(token => {
                                    const retryReq = req.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${token}`
                                        }
                                    });
                                    return next(retryReq);
                                })
                            );
                        }

                        // Tentar refresh token
                        return authService.refreshToken().pipe(
                            switchMap(response => {
                                if (response.success && response.data) {
                                    const retryReq = req.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${response.data.accessToken}`
                                        }
                                    });
                                    return next(retryReq);
                                }
                                authService.logout();
                                return throwError(() => err);
                            }),
                            catchError(refreshErr => {
                                alertService.error('👾 Session expired', 'Please login again');
                                authService.logout();
                                return EMPTY;
                            })
                        );

                    case 403:
                        console.error('🦈 Request 403:', err);
                        alertService.error('🔒 Forbidden request', 'You are not allowed to access this resource');
                        return EMPTY;

                    case 404:
                        console.error('🦈 Request 404:', err);
                        alertService.error('👾 Resource not found', 'Please check the URL');
                        return throwError(() => err);

                    case 500:
                        console.error('🦈 Internal server error:', err);
                        alertService.error('Internal server error', 'Please try again later');
                        return throwError(() => err);

                    default:
                        console.error('An error occurred:', err);
                        return throwError(() => err);
                }
            }

            // Handle non-HTTP errors
            console.error('An error occurred:', err);
            return throwError(() => err);
        })
    );
};